import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useParams } from "react-router-dom";
import {
	createShareComment,
	getShareIssue,
	LinearIssueDetail,
	LinearLabel,
	replaceImageUrlsInMarkdown,
} from "../../api/shareApi";
import Markdown from "../components/Markdown/Markdown";
import s from "../styles/shareIssue.module.scss";
import { presentComment } from "../utils/commentPresentation";

function formatDate(raw?: string) {
	if (!raw) return "";
	const d = new Date(raw);
	if (Number.isNaN(d.getTime())) return raw;
	return d.toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

function formatRelative(raw?: string) {
	if (!raw) return "";
	const d = new Date(raw);
	if (Number.isNaN(d.getTime())) return raw;
	const now = Date.now();
	const diff = now - d.getTime();
	const mins = Math.floor(diff / 60_000);
	if (mins < 1) return "just now";
	if (mins < 60) return `${mins}m ago`;
	const hrs = Math.floor(mins / 60);
	if (hrs < 24) return `${hrs}h ago`;
	const days = Math.floor(hrs / 24);
	if (days < 30) return `${days}d ago`;
	return formatDate(raw);
}

function getInitials(name: string): string {
	return name
		.split(/\s+/)
		.map((w) => w[0])
		.filter(Boolean)
		.slice(0, 2)
		.join("")
		.toUpperCase();
}

function extractTeamPrefix(identifier?: string): string | null {
	if (!identifier) return null;
	const match = identifier.match(/^([A-Z][A-Z0-9]*)-/);
	return match ? match[1] : null;
}

const PRIORITY_META: Record<
	number,
	{ label: string; color: string; icon: string }
> = {
	0: { label: "No priority", color: "#6e6e78", icon: "—" },
	1: { label: "Urgent", color: "#f76a6a", icon: "!" },
	2: { label: "High", color: "#f5a623", icon: "↑" },
	3: { label: "Medium", color: "#5e6ad2", icon: "=" },
	4: { label: "Low", color: "#6e6e78", icon: "↓" },
};

function getPriority(issue: LinearIssueDetail) {
	const p = issue.priority ?? 0;
	const meta = PRIORITY_META[p] || PRIORITY_META[0];
	return { ...meta, label: issue.priorityLabel || meta.label };
}

export default function ShareIssuePage() {
	const { token } = useParams<{ token: string }>();
	const [issue, setIssue] = useState<LinearIssueDetail | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
	const [comment, setComment] = useState("");
	const [submitting, setSubmitting] = useState(false);

	const handleContentClick = useCallback((e: React.MouseEvent) => {
		const target = e.target as HTMLElement;
		if (target.tagName === "IMG" && (target as HTMLImageElement).src) {
			e.preventDefault();
			setLightboxSrc((target as HTMLImageElement).src);
		}
	}, []);

	const processedDescription = useMemo(() => {
		if (!issue?.description || !token) return null;
		return replaceImageUrlsInMarkdown(issue.description, token);
	}, [issue?.description, token]);

	const processedComments = useMemo(() => {
		if (!issue?.comments?.length || !token) return {};
		const out: Record<string, string> = {};
		for (const c of issue.comments) {
			out[c.id] = replaceImageUrlsInMarkdown(c.body, token);
		}
		return out;
	}, [issue?.comments, token]);

	useEffect(() => {
		if (!token) {
			setError("Missing share token");
			setLoading(false);
			return;
		}
		getShareIssue(token)
			.then(setIssue)
			.catch((e: any) => setError(e?.message ?? "Link invalid or expired."))
			.finally(() => setLoading(false));
	}, [token]);

	if (loading) {
		return (
			<div className={s.loadingPage}>
				<div className={s.spinner} />
			</div>
		);
	}

	if (error || !issue) {
		return (
			<div className={s.errorPage}>
				<div className={s.errorBox}>
					<div className={s.errorIcon}>&#9888;</div>
					<div className={s.errorText}>
						{error ?? "This link is invalid or has expired."}
					</div>
				</div>
			</div>
		);
	}

	const teamPrefix = extractTeamPrefix(issue.identifier);
	const priority = getPriority(issue);

	return (
		<div className={`internal-root ${s.page}`}>
			{/* Top navigation bar */}
			<nav className={s.navbar}>
				{teamPrefix && (
					<>
						<span className={s.navTeam}>
							<span
								className={s.navTeamIcon}
								style={{
									background: issue.state?.color || "#6e6e78",
								}}
							>
								{teamPrefix[0]}
							</span>
							{teamPrefix}
						</span>
						<span className={s.navSep}>/</span>
					</>
				)}
				<span className={s.navIssue}>
					{issue.identifier} {issue.title}
				</span>
			</nav>

			<div className={s.layout}>
				{/* Content */}
				<div className={s.container}>
					{/* Issue header */}
					<header className={s.issueHeader}>
						<div className={s.identifierRow}>
							<span className={s.identifier}>{issue.identifier}</span>
							{issue.state && (
								<span className={s.stateBadge}>
									<span
										className={s.stateDot}
										style={{
											background: issue.state.color || "#6e6e78",
										}}
									/>
									{issue.state.name}
								</span>
							)}
						</div>
						<h1 className={s.issueTitle}>{issue.title}</h1>

						{(issue.createdAt || issue.updatedAt || issue.botActor?.userDisplayName || issue.creator?.name) && (
							<div className={s.metaRow}>
								{issue.createdAt && (
									<span className={s.metaItem}>
										Created {formatRelative(issue.createdAt)}
									</span>
								)}
								{(issue.botActor?.userDisplayName || issue.creator?.name) && (
									<span className={s.metaItem}>
										by{" "}
										<strong>
											{issue.botActor?.userDisplayName || issue.creator?.name}
										</strong>
									</span>
								)}
								{issue.updatedAt && (
									<span className={s.metaItem}>
										Updated {formatRelative(issue.updatedAt)}
									</span>
								)}
							</div>
						)}
					</header>

					<hr className={s.divider} />

					{/* Description */}
					<div className={s.section}>
						<div className={s.sectionLabel}>Description</div>
						<div className={s.description} onClick={handleContentClick}>
							{issue.description ? (
								<Markdown text={processedDescription ?? issue.description} />
							) : (
								<span className={s.emptyState}>No description</span>
							)}
						</div>
					</div>

					<hr className={s.divider} />

					{/* Comments */}
					<div className={s.section}>
						<div className={s.sectionLabel}>Comments</div>
						{issue.comments.length === 0 ? (
							<div className={s.emptyState}>No comments yet.</div>
						) : (
							<div className={s.comments}>
								{issue.comments.map((c) => {
									const displayBody = processedComments[c.id] ?? c.body;
									const authorName =
										c.botActor?.userDisplayName || c.user?.name;
									const presented = presentComment(authorName, displayBody);
									return (
										<div key={c.id} className={s.comment}>
											<div className={s.commentHeader}>
												<span className={s.commentAvatar}>
													{getInitials(presented.displayAuthor)}
												</span>
												<span className={s.commentAuthor}>
													{presented.displayAuthor}
												</span>
												<span className={s.commentTime}>
													{formatRelative(c.createdAt)}
												</span>
											</div>
											<div
												className={s.commentBody}
												onClick={handleContentClick}
											>
												<Markdown text={presented.displayBody} />
											</div>
										</div>
									);
								})}
							</div>
						)}

						<form
							className={s.composer}
							onSubmit={async (e) => {
								e.preventDefault();
								const body = comment.trim();
								if (!body || !token) return;
								try {
									setSubmitting(true);
									await createShareComment(token, body);
									setComment("");
									const refreshed = await getShareIssue(token);
									setIssue(refreshed);
								} catch {
									// silently fail
								} finally {
									setSubmitting(false);
								}
							}}
						>
							<textarea
								className={s.composerInput}
								placeholder="Write a comment..."
								value={comment}
								onChange={(e) => setComment(e.target.value)}
								rows={3}
							/>
							<button
								className="btn-primary"
								type="submit"
								disabled={submitting || !comment.trim()}
							>
								{submitting ? "Sending…" : "Send"}
							</button>
						</form>
					</div>
				</div>

				{/* Properties sidebar */}
				<aside className={s.sidebar}>
					<div className={s.sidebarGroup}>
						<div className={s.sidebarGroupTitle}>Properties</div>
						<div className={s.propertyList}>
							{issue.state && (
								<div className={s.propertyRow}>
									<span
										className={s.propertyIcon}
										style={{ background: issue.state.color || "#6e6e78" }}
									>
										<StatusIcon type={issue.state.type} />
									</span>
									<span className={s.propertyValue}>{issue.state.name}</span>
								</div>
							)}
							<div className={s.propertyRow}>
								<span
									className={s.propertyIcon}
									style={{ background: priority.color }}
								>
									<span
										style={{ color: "#fff", fontWeight: 700, fontSize: 11 }}
									>
										{priority.icon}
									</span>
								</span>
								<span className={s.propertyValue}>{priority.label}</span>
							</div>
							{issue.assignee ? (
								<div className={s.propertyRow}>
									<span
										className={s.propertyIcon}
										style={{ background: "var(--fill-strong)" }}
									>
										<span
											style={{
												color: "var(--text-muted)",
												fontWeight: 600,
												fontSize: 10,
											}}
										>
											{getInitials(
												issue.assignee.displayName || issue.assignee.name,
											)}
										</span>
									</span>
									<span className={s.propertyValue}>
										{issue.assignee.displayName || issue.assignee.name}
									</span>
								</div>
							) : (
								<div className={s.propertyRow}>
									<span
										className={s.propertyIcon}
										style={{ background: "var(--fill-default)" }}
									>
										<span
											style={{ color: "var(--text-faint)", fontSize: 12 }}
										>
											—
										</span>
									</span>
									<span className={s.propertyMuted}>No assignee</span>
								</div>
							)}
						</div>
					</div>

					{issue.labels && issue.labels.length > 0 && (
						<div className={s.sidebarGroup}>
							<div className={s.sidebarGroupTitle}>Labels</div>
							<div className={s.labelList}>
								{issue.labels.map((label: LinearLabel) => (
									<span key={label.id} className={s.labelChip}>
										<span
											className={s.labelDot}
											style={{ background: label.color || "#6e6e78" }}
										/>
										{label.name}
									</span>
								))}
							</div>
						</div>
					)}
				</aside>
			</div>

			{lightboxSrc && (
				<ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
			)}
		</div>
	);
}

/* ─── Fullscreen image lightbox with zoom & pan ─── */

function ImageLightbox({ src, onClose }: { src: string; onClose: () => void }) {
	const imgRef = useRef<HTMLImageElement>(null);
	const [scale, setScale] = useState(1);
	const [translate, setTranslate] = useState({ x: 0, y: 0 });
	const dragging = useRef(false);
	const lastPos = useRef({ x: 0, y: 0 });

	const resetView = useCallback(() => {
		setScale(1);
		setTranslate({ x: 0, y: 0 });
	}, []);

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [onClose]);

	const handleWheel = useCallback((e: React.WheelEvent) => {
		e.stopPropagation();
		setScale((prev) => {
			const next = prev * (e.deltaY < 0 ? 1.15 : 1 / 1.15);
			const clamped = Math.min(Math.max(next, 0.5), 8);
			if (clamped <= 1) setTranslate({ x: 0, y: 0 });
			return clamped;
		});
	}, []);

	const handlePointerDown = useCallback((e: React.PointerEvent) => {
		if (e.button !== 0) return;
		dragging.current = true;
		lastPos.current = { x: e.clientX, y: e.clientY };
		(e.target as HTMLElement).setPointerCapture(e.pointerId);
	}, []);

	const handlePointerMove = useCallback(
		(e: React.PointerEvent) => {
			if (!dragging.current || scale <= 1) return;
			const dx = e.clientX - lastPos.current.x;
			const dy = e.clientY - lastPos.current.y;
			lastPos.current = { x: e.clientX, y: e.clientY };
			setTranslate((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
		},
		[scale],
	);

	const handlePointerUp = useCallback(() => {
		dragging.current = false;
	}, []);

	const handleOverlayClick = useCallback(
		(e: React.MouseEvent) => {
			if (e.target === e.currentTarget) {
				onClose();
			}
		},
		[onClose],
	);

	const handleDoubleClick = useCallback(() => {
		if (scale > 1) {
			resetView();
		} else {
			setScale(2.5);
		}
	}, [scale, resetView]);

	return (
		<div className={s.lightboxOverlay} onClick={handleOverlayClick}>
			<button className={s.lightboxClose} onClick={onClose} aria-label="Close">
				&times;
			</button>
			<div className={s.lightboxContent} onWheel={handleWheel}>
				<img
					ref={imgRef}
					className={s.lightboxImg}
					src={src}
					alt=""
					draggable={false}
					style={{
						transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
						cursor: scale > 1 ? "grab" : "zoom-in",
					}}
					onPointerDown={handlePointerDown}
					onPointerMove={handlePointerMove}
					onPointerUp={handlePointerUp}
					onDoubleClick={handleDoubleClick}
				/>
			</div>
			<div className={s.lightboxZoomHint}>
				Scroll to zoom &middot; Double-click to{" "}
				{scale > 1 ? "reset" : "zoom in"}
			</div>
		</div>
	);
}

function StatusIcon({ type }: { type?: string }) {
	const fill = "#fff";
	const size = 10;

	switch (type) {
		case "started":
			return (
				<svg width={size} height={size} viewBox="0 0 12 12" fill="none">
					<circle
						cx="6"
						cy="6"
						r="5"
						stroke={fill}
						strokeWidth="2"
						fill="none"
					/>
					<path d="M6 1A5 5 0 0 1 6 11" fill={fill} />
				</svg>
			);
		case "completed":
			return (
				<svg width={size} height={size} viewBox="0 0 12 12" fill="none">
					<path
						d="M4.5 6.5L5.5 7.5L7.5 4.5"
						stroke={fill}
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			);
		case "cancelled":
			return (
				<svg width={size} height={size} viewBox="0 0 12 12" fill="none">
					<path
						d="M4 4L8 8M8 4L4 8"
						stroke={fill}
						strokeWidth="1.5"
						strokeLinecap="round"
					/>
				</svg>
			);
		default:
			return (
				<svg width={size} height={size} viewBox="0 0 12 12" fill="none">
					<circle
						cx="6"
						cy="6"
						r="4.5"
						stroke={fill}
						strokeWidth="1.5"
						fill="none"
					/>
				</svg>
			);
	}
}
